import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { Building2 } from 'lucide-react';
import { BusinessInfoPanel } from '@/components/business-info-panel';
import { LocationEvidencePanel } from '@/components/location-evidence-panel';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import type { SiteDetailResponse } from '@/lib/types';

// =====================================================================
// BusinessInfo — WP1 Task 7 move of site-business-info.tsx. Canonical NAP
// identity editor for a site (WS1 S3). Route: locationRoutes.businessInfo(businessId, siteId)
// ("/app/b/:siteId/foundation/business-info"). Drops its own back-link and
// <DashboardShell> — the layer-shell layout (PanelShell) supplies both.
// =====================================================================

export function BusinessInfo() {
  const meState = useMe();
  const { businessId = '', locationId: routeSiteId } = useTenantParams();
  // Fix round 1 (task review MEDIUM-1): restores site-business-info.tsx's
  // original gating — panels mount only once /api/sites/:id has resolved
  // SUCCESSFULLY, not straight off the raw route param. A stale/wrong-org
  // siteId in the URL must not let BusinessInfoPanel/LocationEvidencePanel
  // each independently fire their own fetch and fail; the shared fetch here
  // is the single gate, same as the pre-move component.
  const [siteId, setSiteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !routeSiteId) return;
    void api
      .get<SiteDetailResponse>(locationApi(businessId, routeSiteId, ''))
      .then((res) => {
        setSiteId(res.site.id);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Could not load site.'),
      );
  }, [businessId, routeSiteId]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Foundation"
        title="Business info"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready') {
    return null;
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Foundation"
        title="Business info"
        actions={
          <div className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-card px-3 py-1 text-xs text-text-secondary">
            <Building2 className="h-3 w-3" aria-hidden="true" />
            Name · address · phone
          </div>
        }
      />

      {error ? (
        <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</div>
      ) : null}

      {siteId ? (
        <>
          <BusinessInfoPanel siteId={siteId} />
          <LocationEvidencePanel siteId={siteId} />
        </>
      ) : (
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
      )}
    </PageContainer>
  );
}
